<script lang="ts" module>
	import * as _ from 'radashi'
	import type { Event } from '#library/types.ts'
	import Image from './image.svelte'
	import { format_date } from 'kitto'
	import Date from '#components/date.svelte'
	import { page } from '$app/state'

	export const colors = new Map([['Theatre', 'theme-lupton-on-forget-me-not']])

	interface Props {
		event: Event
		view?: 'grid' | 'calendar' | 'date'
	}
</script>

<script lang="ts">
	let { event, view = 'grid' }: Props = $props()

	const { image, dates } = $derived(event.content)

	const caption = $derived.by(() => {
		return {
			name: event.content.category,
			color: colors.get(event.content.category!) || 'theme-lupton-on-pollen'
		}
	})

	const venue = $derived(dates?.[0]?.venue)
	const ishome = $derived(page.url.pathname === '/')
</script>

{#if view !== 'grid' && view === 'calendar'}
	<Date
		image={image?.filename
			? { filename: image.filename, alt: image.alt || event.name, focus: image.focus || '' }
			: undefined}
		{dates}
		price={dates?.[0]?.price}
		event={event.name}
		href="/{event.full_slug}"
		theme="theme-rose-on-white"
	/>
{:else if view === 'date'}
	<a href="/{event.full_slug}" class="date">
		{#if dates?.length}
			{@const rdates = ishome ? dates : [dates[0]]}
			{#each rdates as date, i (i)}
				{#if date.start}
					<time datetime={date.start}>
						<div class="month">{format_date('{MMM}', date.start)}</div>
						<div class="day">{format_date('{DD}', date.start)}</div>
					</time>
				{/if}
			{/each}
		{/if}
		<div>
			<div class="title">{event.name}</div>
			<div class="subtitle">{format_date('{HH}:{mm}', dates?.[0]?.start)} - {venue}</div>
		</div>
	</a>
{:else}
	<a class="card theme-rose-on-white" href="/{event.full_slug}">
		<figure class="figure">
			{#if image?.filename}
				<Image
					src={image.filename}
					alt={image.alt || event.name}
					focus={image.focus || ''}
					aspect="1/1"
					fill
				/>
			{:else}
				<div class="fallback">
					<svg class="fallback-icon">
						<use xlink:href="/images/vector.svg#icon"></use>
					</svg>
				</div>
			{/if}
			{#if caption}
				<figcaption class="caption {caption.color}">{caption.name}</figcaption>
			{/if}
		</figure>
		{#if dates?.length && dates[0]?.start}
			<div class="suptitle">
				{#each dates as date, i (i)}
					{#if date.start}
						{format_date('{DD} {MMM} {YYYY}', date.start)}
						{#if i < dates.length - 1}
							&nbsp;-&nbsp;
						{/if}
					{/if}
				{/each}
			</div>
		{/if}
		<div class="title">{event.name}</div>
		{#if venue}
			<div class="subtitle">{venue}</div>
		{/if}
	</a>
{/if}

<style lang="css">
	.fallback {
		aspect-ratio: 1/1;
		background-color: var(--c-foreground);
		color: var(--c-gimson);
		padding: 5rem;
	}

	.fallback-icon {
		rotate: -45deg;
	}

	.date {
		.month {
			color: var(--c-foreground);
			font: 400 var(--f-body2);
			letter-spacing: var(--ls-body2);
			text-transform: uppercase;
		}

		.day {
			color: var(--c-foreground);
			font: 400 var(--f-h1);
			letter-spacing: var(--ls-h1);

			@media (--from-mobile) {
				margin-block-end: 2.5rem;
			}
		}

		.title {
			color: var(--c-midground);
			font: 700 var(--f-h6);
			letter-spacing: var(--ls-h6);

			@media (--from-mobile) {
				margin-block-start: 0.625rem;
			}
		}

		.subtitle {
			color: var(--c-midground);
			font: 400 var(--f-body2);
			letter-spacing: var(--ls-body2);
			margin-block-start: 0.75rem;
		}
	}

	.card {
		color: var(--c-midground);
	}

	.caption {
		background-color: var(--c-background);
		color: var(--c-foreground);
		font: 500 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		padding: 0.375rem 1rem;
		text-align: center;
		text-transform: uppercase;
	}

	.suptitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 1rem;
		text-transform: uppercase;

		@media (--from-mobile) {
			margin-block-start: 1.75rem;
		}
	}

	.title {
		font: 600 var(--f-h6);
		letter-spacing: var(--ls-h6);

		@media (--from-mobile) {
			margin-block-start: 0.625rem;
		}
	}

	.subtitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-start: 0.75rem;
	}
</style>
